import * as React from "react";
import { cn } from "../../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { useCurrencyOptional, type Money } from "../../hooks/use-currency";

export interface AmountProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: number | null | undefined;
  currency?: string;
  decimals?: number;
  fallback?: string;
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Reactive display-currency store (triggers re-renders in Amount)
// ---------------------------------------------------------------------------
let _displayCurrency = "";
const _listeners = new Set<() => void>();

export function setActiveDisplayCurrency(currency: string) {
  if (_displayCurrency === currency) return;
  _displayCurrency = currency;
  _listeners.forEach((l) => l());
}

export function getActiveDisplayCurrency(): string {
  return _displayCurrency;
}

function useActiveDisplayCurrency(): string {
  return React.useSyncExternalStore(
    (cb) => {
      _listeners.add(cb);
      return () => {
        _listeners.delete(cb);
      };
    },
    () => _displayCurrency,
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
export function formatAmount(
  value: number | null | undefined,
  options?: {
    currency?: string;
    decimals?: number;
    fallback?: string;
    compact?: boolean;
  },
): string {
  const {
    currency,
    decimals = 2,
    fallback = "-",
    compact = false,
  } = options ?? {};

  if (value == null) return fallback;

  const formatted = compact
    ? Intl.NumberFormat(undefined, {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value)
    : value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

  return currency ? `${currency} ${formatted}` : formatted;
}

function formatNum(value: number, decimals: number, compact: boolean): string {
  if (compact) {
    return Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ---------------------------------------------------------------------------
// Amount component
// ---------------------------------------------------------------------------
export const Amount = React.forwardRef<HTMLSpanElement, AmountProps>(
  (
    {
      value,
      currency,
      decimals = 2,
      fallback = "-",
      compact = false,
      className,
      ...props
    },
    ref,
  ) => {
    const currencyCtx = useCurrencyOptional();
    const globalDisplayCurrency = useActiveDisplayCurrency();
    const isEmpty = value == null;

    const displayCurrency =
      currencyCtx?.displayCurrency || globalDisplayCurrency;
    const isDifferentCurrency =
      !isEmpty &&
      !!currency &&
      !!displayCurrency &&
      currency !== displayCurrency;

    let text: string;
    let convertedText: string | null = null;

    if (isEmpty) {
      text = fallback;
    } else if (currency && currencyCtx) {
      const money: Money = { currency, amount: value };
      const converted = currencyCtx.convert(money);
      if (converted && converted.converted.currency !== currency) {
        text = `${converted.converted.currency} ${formatNum(converted.converted.amount, decimals, compact)}`;
        convertedText = `${currency} ${formatNum(value, decimals, compact)}`;
      } else {
        text = `${currency} ${formatNum(value, decimals, compact)}`;
      }
    } else {
      text = formatAmount(value, { currency, decimals, fallback, compact });
    }

    const originalLabel =
      convertedText ??
      (isDifferentCurrency && currency
        ? `${currency} ${formatNum(value!, decimals, compact)}`
        : null);

    const span = (
      <span
        ref={ref}
        className={cn(
          "tabular-nums inline-flex items-center",
          isEmpty ? "text-slate-400 dark:text-slate-500" : "font-medium",
          className,
        )}
        {...props}
      >
        {text}
        {isDifferentCurrency && (
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400 ml-1 shrink-0" />
        )}
      </span>
    );

    if (isDifferentCurrency && originalLabel) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{span}</TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Original: {originalLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return span;
  },
);

Amount.displayName = "Amount";
